import { useEffect, useState } from "react";

const STAGES = ["parked", "requested", "fetching", "ready", "completed"];

const ValetJourneyAnimation = ({ status }) => {
  const stageIndex = STAGES.indexOf(status);
  const [animPhase, setAnimPhase] = useState(stageIndex);

  useEffect(() => {
    setAnimPhase(STAGES.indexOf(status));
  }, [status]);

  // Positions: 0=parked(left), 1=requested(valet walks), 2=fetching(car mid), 3=ready(car at exit), 4=done
  const carLeft = animPhase <= 1 ? 8 : animPhase === 2 ? 50 : animPhase >= 3 ? 88 : 8;
  const valetLeft = animPhase === 0 ? 85 : animPhase === 1 ? 20 : -10;
  const showValet = animPhase <= 1;
  const showCar = animPhase < 4;
  const showCheck = animPhase >= 4;

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-b from-[#0D0E14] to-[#12131A] border border-white/5" data-testid="valet-journey-animation">
      {/* Sky / Scene */}
      <div className="relative h-40 overflow-hidden">
        {/* Stars */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-white/30"
              style={{
                left: `${8 + (i * 7.3) % 90}%`,
                top: `${5 + (i * 11) % 30}%`,
                animationDelay: `${i * 0.4}s`,
                animation: "twinkle 2s ease-in-out infinite alternate"
              }}
            />
          ))}
        </div>

        {/* Parking structure (left) */}
        <div className="absolute left-2 bottom-8 w-16 h-20">
          <div className="w-full h-full rounded-t-lg bg-gradient-to-b from-gray-700/40 to-gray-800/40 border border-white/5 flex flex-col items-center justify-end pb-1 gap-0.5">
            <div className="text-[7px] text-gray-500 font-bold tracking-wider">PARKING</div>
            <div className="w-10 h-5 rounded-sm bg-gray-900/60 border border-white/5" />
            <div className="w-10 h-5 rounded-sm bg-gray-900/60 border border-white/5" />
          </div>
        </div>

        {/* Exit / Venue (right) */}
        <div className="absolute right-2 bottom-8 w-16 h-20">
          <div className="w-full h-full rounded-t-lg bg-gradient-to-b from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/20 flex flex-col items-center justify-end pb-1">
            <div className="text-[7px] text-[#D4AF37]/70 font-bold tracking-wider">EXIT</div>
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mt-1">
              <svg className="w-4 h-4 text-[#D4AF37]/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
          </div>
        </div>

        {/* Road */}
        <div className="absolute bottom-4 left-0 right-0 h-8">
          <div className="absolute inset-0 bg-gray-800/60 rounded-sm" />
          {/* Road dashes */}
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex items-center justify-center gap-3 px-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="w-4 h-0.5 bg-yellow-600/30 rounded-full shrink-0" />
            ))}
          </div>
        </div>

        {/* Valet figure */}
        {showValet && (
          <div
            className="absolute bottom-5 transition-all duration-[2000ms] ease-in-out"
            style={{ left: `${valetLeft}%`, transform: "translateX(-50%)" }}
          >
            <div className="relative">
              {/* Body */}
              <div className="w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <svg className="w-3 h-3 text-black" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="4" r="3" />
                  <path d="M12 7c-3 0-4 2-4 4v3h8v-3c0-2-1-4-4-4z" />
                </svg>
              </div>
              {/* Walking animation dots */}
              {animPhase === 1 && (
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className="w-1 h-1 rounded-full bg-[#D4AF37]/40"
                      style={{ animation: `bounce 0.6s ${i * 0.2}s ease-in-out infinite` }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Car */}
        {showCar && (
          <div
            className="absolute bottom-5 transition-all duration-[2500ms] ease-in-out"
            style={{ left: `${carLeft}%`, transform: "translateX(-50%)" }}
          >
            <div className={`relative ${animPhase >= 3 ? "animate-pulse" : ""}`}>
              <svg width="32" height="18" viewBox="0 0 32 18" fill="none">
                {/* Car body */}
                <rect x="2" y="6" width="28" height="9" rx="2" fill={animPhase >= 3 ? "#D4AF37" : "#6B7280"} opacity={animPhase >= 3 ? "1" : "0.7"} />
                {/* Roof */}
                <path d="M8 6 L12 1 L22 1 L26 6" fill={animPhase >= 3 ? "#C4A030" : "#4B5563"} opacity="0.8" />
                {/* Windows */}
                <path d="M12.5 2 L9.5 5.5 L16 5.5 Z" fill="#1E293B" opacity="0.6" />
                <path d="M17 2 L17 5.5 L24.5 5.5 L22 2 Z" fill="#1E293B" opacity="0.6" />
                {/* Wheels */}
                <circle cx="8" cy="15" r="2.5" fill="#1E293B" stroke={animPhase >= 3 ? "#D4AF37" : "#6B7280"} strokeWidth="1" />
                <circle cx="24" cy="15" r="2.5" fill="#1E293B" stroke={animPhase >= 3 ? "#D4AF37" : "#6B7280"} strokeWidth="1" />
                {/* Headlights */}
                {animPhase >= 2 && (
                  <>
                    <rect x="28" y="8" width="3" height="2" rx="1" fill="#FDE68A" opacity="0.9" />
                    <ellipse cx="33" cy="9" rx="4" ry="3" fill="#FDE68A" opacity="0.1" />
                  </>
                )}
              </svg>
              {/* Exhaust when moving */}
              {animPhase === 2 && (
                <div className="absolute -left-3 bottom-0 flex gap-0.5">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-gray-500/20"
                      style={{ animation: `fadeOut 1s ${i * 0.3}s ease-out infinite` }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Completion check */}
        {showCheck && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center" style={{ animation: "scaleIn 0.5s ease-out" }}>
              <svg className="w-8 h-8 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" style={{ strokeDasharray: 30, strokeDashoffset: 0, animation: "drawCheck 0.6s ease-out" }} />
              </svg>
            </div>
          </div>
        )}

        {/* Ready glow */}
        {animPhase === 3 && (
          <div className="absolute right-6 bottom-6 w-20 h-20 rounded-full bg-[#D4AF37]/10" style={{ animation: "pulse-glow 2s ease-in-out infinite" }} />
        )}
      </div>

      {/* Progress Steps */}
      <div className="px-4 pb-4 pt-1">
        <div className="flex items-center justify-between relative">
          {/* Connecting line */}
          <div className="absolute top-3 left-6 right-6 h-0.5 bg-white/5" />
          <div
            className="absolute top-3 left-6 h-0.5 bg-[#D4AF37] transition-all duration-1000 ease-out"
            style={{ width: `${Math.min(stageIndex / (STAGES.length - 2), 1) * (100 - 12)}%` }}
          />

          {STAGES.slice(0, -1).map((stage, i) => {
            const isActive = i <= stageIndex;
            const isCurrent = i === stageIndex;
            return (
              <div key={stage} className="relative z-10 flex flex-col items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isCurrent
                    ? "bg-[#D4AF37] shadow-lg shadow-[#D4AF37]/30 scale-110"
                    : isActive
                    ? "bg-[#D4AF37]/80"
                    : "bg-white/10"
                }`}>
                  {isActive && i < stageIndex ? (
                    <svg className="w-3 h-3 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : (
                    <span className={`text-[8px] font-bold ${isActive ? "text-black" : "text-gray-500"}`}>{i + 1}</span>
                  )}
                </div>
                <span className={`text-[9px] font-medium ${isCurrent ? "text-[#D4AF37]" : isActive ? "text-gray-300" : "text-gray-600"}`}>
                  {stage === "parked" ? "Parked" : stage === "requested" ? "Requested" : stage === "fetching" ? "Fetching" : "Ready"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes twinkle {
          0% { opacity: 0.2; }
          100% { opacity: 0.7; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-3px); opacity: 0.8; }
        }
        @keyframes fadeOut {
          0% { opacity: 0.4; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(-6px) scale(1.5); }
        }
        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes drawCheck {
          0% { stroke-dashoffset: 30; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
};

export default ValetJourneyAnimation;
